import React from "react";
import { startCase, get } from "lodash";
import PropTypes from "prop-types";
import { Header } from "orcs-design-system";
import {
  useWorkspaces,
  useWorkspace,
  useWorkspaceFeatureFlags,
} from "src/contexts/global/WorkspaceContext";
import { useActiveTenant } from "src/contexts/global/ApolloSettingsContext";
import { useAuth, useUserPerson } from "src/contexts/global/GlobalContext";
import { useAuth0 } from "src/contexts/auth0Provider";
import { useFeatureFlags } from "src/contexts/global/FeatureFlagsContext";
import useTagsVisibleInFilter from "src/app/Restricted/useTagsVisibleInFilter";

import EnvInformationBar from "src/components/EnvInformationBar";
import GlobalNotification from "src/components/GlobalNotification";
import SearchContainer from "src/components/Search/SearchContainer";
import { isInternalSupportUser } from "src/services/auth";
import { isLocalEnvironment } from "src/services/localSettings";
import useDefaultRoutePath from "src/app/Restricted/useDefaultRoutePath";
import { displayInitials } from "src/util/personName";
import DirectoryLogo from "src/components/Logo/DirectoryLogo";
import AppName from "src/components/AppName";
import WorkspaceSwitcher from "src/components/Nav/WorkspaceSwitcher";
import Search from "src/components/Search";
import { useAISidebar } from "src/hooks/useAISidebar";
import { useOrgBranding } from "src/hooks/orgConfig/hooks/useOrgBranding";
import EmptyAppNav from "./EmptyAppNav";
import UserMenuContent from "./UserMenuContent";

const FullAppNav = ({ themeSwitcher }) => {
  const defaultRoutePath = useDefaultRoutePath();
  const { user, logout } = useAuth0();
  const activeTenant = useActiveTenant();
  const auth = useAuth();
  const userPerson = useUserPerson();
  const workspaces = useWorkspaces();
  const workspace = useWorkspace();

  const {
    branding: { appName, logoImage },
  } = useOrgBranding();

  const featureFlags = useFeatureFlags();
  const tagsVisibleIn = useTagsVisibleInFilter();

  const { preferredUsername } = auth;

  const avatarSource = get(userPerson, "avatar");

  const isDemo = !!window?.location?.search?.includes("demo=true");

  const isSupportUser = isInternalSupportUser(user) || isLocalEnvironment();

  const showLogoSeparator = logoImage && (workspaces.length > 1 || appName);

  const { enableAIChat } = useWorkspaceFeatureFlags();

  const { toggleAI } = useAISidebar();

  return (
    <>
      {activeTenant && (
        <EnvInformationBar
          user={user}
          overrideTenant={activeTenant}
          isDemo={isDemo}
        />
      )}
      <GlobalNotification />
      <Header
        variant="search"
        logo={<DirectoryLogo workspace={workspace} />}
        appName={
          appName ? <AppName defaultRoutePath={defaultRoutePath} /> : undefined
        }
        currentWorkspace={
          workspaces.length > 1 && (
            <WorkspaceSwitcher
              workspaces={workspaces}
              isSupportUser={isSupportUser}
              withCustomStyles
              isSearchable={true}
            />
          )
        }
        userName={startCase(preferredUsername)}
        avatarSource={avatarSource}
        avatarAlt={`Avatar for ${startCase(preferredUsername)}`}
        avatarInitials={userPerson && displayInitials(userPerson)}
        {...(enableAIChat && { onAIClick: toggleAI })}
        searchComponent={
          <SearchContainer
            workspace={workspace}
            featureFlags={featureFlags}
            tagsVisibleIn={tagsVisibleIn}
            dropdownOptionsDisplayLimit={3}
            Search={(searchProps) => <Search {...searchProps} />}
          />
        }
        userMenuContent={
          <UserMenuContent themeSwitcher={themeSwitcher} logout={logout} />
        }
        showLogoSeparator={showLogoSeparator}
      />
    </>
  );
};

FullAppNav.propTypes = {
  themeSwitcher: PropTypes.func,
};

// TODO: Duplication with code in the ErrorPageWrapper and AppNav
// because for some reason adding AppNav to ErrorPageWrapper causes circular
// dependency that was not quick to figure out.
const EmptyAppNavWithAuth = () => {
  const { logout, user } = useAuth0();
  return <EmptyAppNav user={user} logout={logout} />;
};

const AppNav = (props) => {
  const { emptyMode = false } = props;

  if (emptyMode) {
    return <EmptyAppNavWithAuth {...props} />;
  }

  return <FullAppNav {...props} />;
};

AppNav.propTypes = {
  emptyMode: PropTypes.bool,
};

export default AppNav;
